import formatter from '@/lib/formatter';
import {useRef, useState} from 'react';
import {cn} from '@/lib/tailwind-utils';
import {getPriorityInfo, getStatusInfo} from '@/pages/resources/tasks/types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/shadcn_components/ui/tooltip';
import {PinIcon} from 'lucide-react';
import {Button} from '@/shadcn_components/ui/button';
import {calculateDaysDiff} from '@/lib/utils';
import useWorkflowStore from '../../store';

const PlanInfo = ({
  title,
  hidden = false,
  workDays,
  start_date,
  end_date,
  delay,
  delay_tooltip,
}: {
  title: string;
  hidden?: boolean;
  workDays?: number;
  start_date?: Date;
  end_date?: Date;
  delay?: number;
  delay_tooltip?: string;
}) => {
  if (hidden) return null;
  return (
    <div className="">
      <h2 className="text-sm font-semibold text-center text-gray-800">{title}</h2>
      <div className="grid grid-cols-2 gap-2">
        <div>
          {workDays != undefined && <p className="text-xs text-gray-600">Số ngày</p>}
          <p className="text-xs text-gray-600">Bắt đầu:</p>
          <p className="text-xs text-gray-600">Kết thúc:</p>
        </div>
        <div>
          {workDays != undefined && (
            <p className="text-xs text-gray-800">{workDays?.toFixed?.(0) || 0}</p>
          )}
          <p className="text-xs text-gray-800">{formatter.formatDate(start_date)}</p>
          <p className="text-xs text-gray-800">{formatter.formatDate(end_date)}</p>
        </div>
      </div>
      {delay !== undefined && delay > 0 && (
        <div className="flex flex-row items-center text-xs text-red-600">
          <span>
            Chậm {delay} ngày {`(${delay_tooltip})` || `(Trễ ${delay} ngày so với kế hoạch)`}
          </span>
        </div>
      )}
    </div>
  );
};

export const TaskNodeView = ({data}) => {
  const show_details = useWorkflowStore(state => state.options?.show_details);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // Node header content that appears in both views
  const nodeHeader = (
    <div className="flex items-center justify-between gap-2">
      <h3 className={cn('w-64 text-left text-xl font-medium text-gray-700')}>
        {data.name}{' '}
        <span className="text-sm font-normal">({data.expected_work_days} ngày)</span>
      </h3>
      <Button
        onClick={e => {
          e.stopPropagation();
          setIsTooltipVisible(prev => !prev);
        }}
        variant="ghost"
        className={cn(
          isTooltipVisible ? 'border border-primary' : '',
        )}
        title={isTooltipVisible ? 'Ẩn chi tiết' : 'Ghim chi tiết'}>
        <PinIcon className="h-4 w-4" />
      </Button>
    </div>
  );

  // Show details directly if show_details is true or tooltip is pinned
  if (show_details || isTooltipVisible) {
    return (
      <div className="relative" ref={containerRef}>
        <div className="flex flex-col gap-2 rounded-lg p-4 z-50 w-80">
          {nodeHeader}
          <div className="absolute top-[6.5rem] left-0 bg-white border border-1 rounded-lg shadow-lg p-4 z-60 transition-transform">
            <TooltipContentComponent data={data} />
          </div>
        </div>
      </div>
    );
  }

  // Otherwise use tooltip behavior
  return (
    <div className="relative" ref={containerRef}>
      <TooltipProvider>
        <Tooltip
          delayDuration={500}
          open={isHovering}
          onOpenChange={open => {
            setIsHovering(open);
          }}>
          <TooltipTrigger asChild>
            {/* Node chính - KHÔNG thay đổi kích thước */}
            <div
              className="flex flex-col gap-2 rounded-lg p-4 z-50 w-80"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}>
              {nodeHeader}
            </div>
          </TooltipTrigger>
          <TooltipContent 
            side="bottom"
            align="center"
            className="w-80 bg-white p-4 shadow-lg rounded-xl z-60 transition-transform">
            <TooltipContentComponent data={data} />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

const TooltipContentComponent = ({data}) => {
  const priorityInfo = getPriorityInfo(data?.priority);
  const statusInfo = getStatusInfo(data?.status);

  const initialVariance = calculateDaysDiff(
    data?.initial_end_date,
    data?.actual_end_date || new Date(),
  );
  const rescheduledVariance = calculateDaysDiff(
    data?.rescheduled_end_date,
    data?.actual_end_date || new Date(),
  );

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="flex flex-col gap-2 max-w-48">
        <p className="text-sm font-semibold text-gray-700 text-center">Mô tả</p>
        <div className="flex flex-col gap-2 items-center">
          <h3 className="text-xs font-medium text-gray-700">Bên liên quan chính</h3>
          <p className="text-sm font-semibold text-blue-600">{data.stakeholders || ''}</p>
          <h4 className="text-xs font-medium text-gray-700">Tình trạng</h4>
          <p className={cn('text-sm font-semibold', statusInfo.color)}>{statusInfo.text}</p>
          <h3 className="text-xs font-medium text-gray-700">Mức độ ưu tiên:</h3>
          <p className={cn('text-sm font-semibold', priorityInfo.color)}>{priorityInfo.text}</p>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <PlanInfo
          title="Kế hoạch"
          workDays={data.expected_work_days || 0}
          start_date={data.start_date}
          end_date={data.initial_end_date}
          delay={initialVariance}
          delay_tooltip={data.actual_end_date ? 'So với kết thúc thực tế' : 'So với hôm nay'}
          hidden={data.options?.hide_initial_plan}
        />
        <PlanInfo
          title="Kế hoạch điều chỉnh"
          workDays={data.rescheduled_work_days || 0}
          start_date={data.rescheduled_start_date}
          end_date={data.rescheduled_end_date}
          delay={rescheduledVariance}
          delay_tooltip={data.actual_end_date ? 'So với kết thúc thực tế' : 'So với hôm nay'}
          hidden={data.options?.hide_rescheduled}
        />
        <PlanInfo
          title="Thực tế"
          workDays={data.actual_work_days || 0}
          start_date={data.actual_start_date}
          end_date={data.actual_end_date}
          // delay={actualVariance}
          // delay_tooltip="So với kế hoạch điều chỉnh"
          hidden={data.options?.hide_actual}
        />
      </div>
    </div>
  );
};
